import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* ── Small sub-components ────────────────────────────────────────────────── */
function ChevronLeft() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="9,18 15,12 9,6" />
    </svg>
  );
}
function EyeIcon({ off }) {
  return off ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

/* ── Slice URL cache ──────────────────────────────────────────────────────── */
const urlCache = new Map();

async function fetchSliceUrl(jobId, sliceIndex, type, authToken) {
  const key = `${jobId}:${sliceIndex}:${type}`;
  if (urlCache.has(key)) return urlCache.get(key);
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const res = await axios.get(
    `${API_BASE}/api/job/${jobId}/slice/${sliceIndex}`,
    { params: { type }, headers }
  );
  const url = res.data.url;
  urlCache.set(key, url);
  return url;
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function ScanViewer({ jobId, jobData, goToSlice }) {
  const { token } = useContext(AuthContext);
  const {
    total_slices   = 0,
    saved_slices   = [],   // array of slice indices that have annotated PNGs
    nodule_details = [],   // [{ slice_idx, location_x, location_y, diameter_mm, confidence }]
  } = jobData || {};

  // `saved_slices` is the sorted array of slice indices available in S3
  const slices = saved_slices.length > 0
    ? saved_slices
    : Array.from({ length: total_slices }, (_, i) => i);

  const [sliderPos, setSliderPos] = useState(0);         // index into `slices` array
  const [viewMode,  setViewMode]  = useState("annotated");
  const [imageUrl,  setImageUrl]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [imgError,  setImgError]  = useState(false);

  const currentSliceIndex = slices[sliderPos] ?? 0;

  /* ── Jump to a specific slice when parent requests it ─────────────────── */
  useEffect(() => {
    if (goToSlice == null) return;
    const pos = slices.indexOf(goToSlice);
    if (pos !== -1) setSliderPos(pos);
  }, [goToSlice]);

  /* nodule info for current slice */
  const noduleOnSlice = nodule_details.find(
    (n) => (n.slice_idx ?? n.slice_peak) === currentSliceIndex
  );

  /* ── Load image whenever slice or mode changes ───────────────────────────── */
  const loadImageRef = useRef(null);

  const loadImage = useCallback(async () => {
    if (!jobId || slices.length === 0) return;
    setLoading(true);
    setImgError(false);
    setImageUrl(null);
    try {
      const url = await fetchSliceUrl(jobId, currentSliceIndex, viewMode, token);
      setImageUrl(url);
    } catch {
      setImgError(true);
    } finally {
      setLoading(false);
    }
  }, [jobId, currentSliceIndex, viewMode]);

  useEffect(() => {
    loadImageRef.current = loadImage;
  });

  useEffect(() => {
    loadImageRef.current();
  }, [sliderPos, viewMode]);

  /* prefetch adjacent slices */
  useEffect(() => {
    const prefetch = async () => {
      for (const offset of [-1, 1, -2, 2]) {
        const idx = sliderPos + offset;
        if (idx >= 0 && idx < slices.length) {
          fetchSliceUrl(jobId, slices[idx], viewMode, token).catch(() => {});
        }
      }
    };
    prefetch();
  }, [sliderPos, viewMode, jobId, slices]);

  /* ── Keyboard navigation ─────────────────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft")  setSliderPos((p) => Math.max(0, p - 1));
      if (e.key === "ArrowRight") setSliderPos((p) => Math.min(slices.length - 1, p + 1));
      if (e.key === "o" || e.key === "O")
        setViewMode((m) => (m === "original" ? "annotated" : "original"));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [slices.length]);

  /* ── Build nodule-slice set for timeline highlights ─────────────────────── */
  const noduleSliceSet = new Set(nodule_details.map((n) => n.slice_idx ?? n.slice_peak));

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div className="card" style={{ padding: "1.25rem" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
        <p className="card-title" style={{ margin: 0 }}>CT Scan Viewer</p>
        <div style={{ display: "flex", gap: 8 }}>
          {["original", "annotated"].map((mode) => (
            <button
              key={mode}
              className={`btn btn-sm ${viewMode === mode ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setViewMode(mode)}
            >
              <EyeIcon off={mode === "original"} />
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Image area */}
      <div style={{
        background:    "black",
        borderRadius:  "var(--radius-md)",
        overflow:      "hidden",
        position:      "relative",
        aspectRatio:   "1 / 1",
        display:       "flex",
        alignItems:    "center",
        justifyContent:"center",
        minHeight:     320,
        marginBottom:  "1rem",
      }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, color: "var(--text-muted)" }}>
            <span className="spinner" />
            <span style={{ fontSize: "0.8rem" }}>Loading slice…</span>
          </div>
        )}

        {imgError && !loading && (
          <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
            <div style={{ fontSize: "2rem", marginBottom: 8 }}>⚠</div>
            Slice unavailable
          </div>
        )}

        {imageUrl && !loading && !imgError && (
          <img
            src={imageUrl}
            alt={`CT slice ${currentSliceIndex}`}
            onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        )}

        {/* Overlay badges */}
        <div style={{ position: "absolute", top: 10, left: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <span className={`tag ${viewMode === "annotated" ? "tag-blue" : "tag-gray"}`}>
            {viewMode === "annotated" ? "AI Overlay" : "Original"}
          </span>
          {noduleOnSlice && (
            <span className="tag tag-red" style={{ animation: "none" }}>
              ● Nodule{typeof noduleOnSlice.diameter_mm === "number" ? ` ${noduleOnSlice.diameter_mm.toFixed(1)} mm` : " detected"}
            </span>
          )}
        </div>

        {/* Slice number */}
        <div style={{
          position: "absolute", bottom: 10, right: 10,
          background: "rgba(0,0,0,0.6)",
          padding: "3px 9px",
          borderRadius: 4,
          fontSize: "0.75rem",
          color: "#ccc",
          fontFamily: "monospace",
        }}>
          Slice {currentSliceIndex + 1} / {total_slices || slices.length}
        </div>
      </div>

      {/* Navigation slider */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "0.75rem" }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setSliderPos((p) => Math.max(0, p - 1))}
            disabled={sliderPos === 0}
            style={{ padding: "6px 10px" }}
          >
            <ChevronLeft />
          </button>

          <input
            type="range"
            min={0}
            max={Math.max(0, slices.length - 1)}
            value={sliderPos}
            onChange={(e) => setSliderPos(Number(e.target.value))}
            style={{ flex: 1, accentColor: "var(--accent-blue)", cursor: "pointer" }}
          />

          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setSliderPos((p) => Math.min(slices.length - 1, p + 1))}
            disabled={sliderPos === slices.length - 1}
            style={{ padding: "6px 10px" }}
          >
            <ChevronRight />
          </button>
        </div>

        {/* Mini timeline showing nodule positions */}
        {nodule_details.length > 0 && (
          <div>
            <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 6 }}>
              Nodule slices highlighted below ↓
            </p>
            <div style={{
              height:        12,
              background:    "var(--bg-input)",
              borderRadius:  4,
              position:      "relative",
              overflow:      "hidden",
              border:        "1px solid var(--border)",
            }}>
              {/* current position marker */}
              <div style={{
                position:  "absolute",
                top: 0, bottom: 0,
                left:      `${(sliderPos / Math.max(1, slices.length - 1)) * 100}%`,
                width:     3,
                background:"var(--accent-blue)",
                transform: "translateX(-50%)",
                borderRadius: 2,
                transition: "left 0.1s",
              }} />
              {/* nodule markers */}
              {nodule_details.map((n, i) => {
                const sIdx = n.slice_idx ?? n.slice_peak;
                const pos = slices.indexOf(sIdx);
                if (pos === -1) return null;
                return (
                  <div
                    key={i}
                    onClick={() => setSliderPos(pos)}
                    title={`Nodule: ${typeof n.diameter_mm === "number" ? n.diameter_mm.toFixed(1) + " mm, " : ""}confidence ${((n.confidence || 0) * 100).toFixed(0)}%`}
                    style={{
                      position: "absolute",
                      top: "25%", bottom: "25%",
                      left: `${(pos / Math.max(1, slices.length - 1)) * 100}%`,
                      width:     6,
                      background:"var(--accent-tumor)",
                      transform: "translateX(-50%)",
                      borderRadius: 2,
                      cursor:    "pointer",
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Keyboard hint */}
      <p style={{ marginTop: "0.75rem", fontSize: "0.72rem", color: "var(--text-muted)", textAlign: "center" }}>
        ← → arrow keys to navigate · O to toggle overlay
      </p>
    </div>
  );
}
